import { Box, Button, Center, Group, Loader, Stack, Text } from '@mantine/core';
import { IconDownload, IconUserPlus } from '@tabler/icons-react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { useDownloadTranscript } from 'src/meetings/hooks/useDownloadTranscript';
import { useTranscriptAudio } from 'src/meetings/hooks/useTranscriptAudio';
import type { CalendarEvent } from 'src/meetings/models';
import { assignSpeakerPath, type MeetingScope } from 'src/meetings/scope';
import { getSpeakers } from 'src/meetings/utils/speaker-utils';
import PageLoader from 'src/UI/loader/PageLoader';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { AudioPlayer } from './transcript/AudioPlayer';
import { TranscriptSegmentsList } from './transcript/TranscriptSegmentsList';
import { Transcript, TranscriptionStatus } from './transcript/types';
import RecordingProcessingFailureMessage from 'src/meetings/detail/tabs/RecordingProcessingFailureMessage';

type Props = {
  event: CalendarEvent;
  transcript: Transcript;
  inlineAudioPlayer?: boolean;
  scope?: MeetingScope;
};

export const TranscriptTab = ({ event, transcript, inlineAudioPlayer = false, scope }: Props) => {
  const navigate = useNavigate();
  const { download, isDownloading } = useDownloadTranscript(event.id);
  const audioControls = useTranscriptAudio({
    fileId: transcript?.file?.id,
    segments: transcript?.segments ?? [],
    eventId: event.id,
  });

  if (event.hasTranscription && transcript?.status !== TranscriptionStatus.READY) {
    return <PageLoader absolute={false} />;
  }

  if (audioControls.isLoadingAudio && event.hasTranscription) {
    return <PageLoader absolute={false} />;
  }

  if (!event.hasTranscription || !transcript) {
    return (
      <Box p="md">
        <Text size="sm" c="gray.7">
          No recording available for this meeting.
        </Text>
      </Box>
    );
  }

  if (transcript) {
    if (transcript.status === TranscriptionStatus.PROCESSING) {
      return (
        <Center h="100%">
          <Stack align="center" gap="md">
            <Loader />
            <Text size="sm" c="gray.7" ta="center">
              Your recording is being transcribed.
            </Text>
          </Stack>
        </Center>
      );
    }

    if (transcript.status === TranscriptionStatus.FAILED) {
      return <RecordingProcessingFailureMessage />;
    }

    if (transcript.status === TranscriptionStatus.EMPTY) {
      return (
        <Center h="100%">
          <Stack align="center" gap="md" maw={400}>
            <Text size="sm" c="gray.7" ta="center">
              No speech was detected in this recording.
            </Text>
          </Stack>
        </Center>
      );
    }

    if (transcript.status === TranscriptionStatus.READY) {
      if (!transcript.segments || !transcript.file) {
        return (
          <Center h="100%">
            <Stack align="center" gap="md" maw={400}>
              <Text size="sm" c="red.7" ta="center" fw={FONT_WEIGHTS.semibold}>
                Transcript data unavailable
              </Text>
              <Text size="sm" c="gray.7" ta="center">
                The transcript appears to be incomplete. Please contact support for assistance.
              </Text>
            </Stack>
          </Center>
        );
      }

      if (audioControls.isLoadingAudio) {
        return <PageLoader />;
      }

      const speakers = scope ? getSpeakers(transcript.segments) : [];
      const unidentifiedSpeakers = speakers.filter(speaker => speaker.status === 'unidentified');
      const firstSpeakerLabel = (unidentifiedSpeakers[0] ?? speakers[0])?.label;

      return (
        <>
          <Group justify="flex-end" px="md" pt="xs" pb="sm">
            {scope && firstSpeakerLabel && (
              <Button
                variant="subtle"
                leftSection={<IconUserPlus size={16} />}
                onClick={() => navigate(assignSpeakerPath(scope, event.id, firstSpeakerLabel))}
              >
                {unidentifiedSpeakers.length > 0 ? 'Identify speakers' : 'Re-assign speakers'}
              </Button>
            )}
            <Button
              variant="subtle"
              leftSection={<IconDownload size={16} />}
              loading={isDownloading}
              onClick={download}
            >
              Download transcript
            </Button>
          </Group>
          <Box pb={120}>
            <TranscriptSegmentsList
              segments={transcript.segments}
              selectedIndex={audioControls.selectedSegmentIndex ?? 0}
              onSegmentClick={audioControls.handleSegmentClick}
            />
          </Box>
          {!audioControls.hasAudioError &&
            (() => {
              const player = (
                <AudioPlayer
                  isPlaying={audioControls.isPlaying}
                  currentTime={audioControls.currentTime}
                  duration={audioControls.duration}
                  isMuted={audioControls.isMuted}
                  onPlayPause={audioControls.handlePlayPause}
                  onSeek={audioControls.handleSeek}
                  onMuteToggle={audioControls.handleMuteToggle}
                  inline={inlineAudioPlayer}
                />
              );
              return inlineAudioPlayer ? player : createPortal(player, document.body);
            })()}
        </>
      );
    }
  }

  // Fallback - shouldn't reach here
  return (
    <Box p="md">
      <Text size="sm" c="gray.7">
        No transcript available for this meeting.
      </Text>
    </Box>
  );
};
